/**
 * The Edge side of the rollover planner: what a technician's workday IS, from
 * the canonical dated authorities, and how a plan becomes response parts.
 *
 * `rolloverSegmentPlanner.ts` is pure and asks one question — "what is this
 * date?" — so that the browser can run the same plan. This module answers it
 * on the server, from:
 *
 *   - resolveDatedShiftEnvelope  per-date hours (both key shapes) + time_off
 *                                (a technician with NO per-day map at all
 *                                falls back to the flat columns — FLOYD-7)
 *   - shop holidays              a holiday is not a workday, whatever the hours say
 *   - recurring + dated breaks   never capacity
 *   - partial-day absences       never capacity (Phase 10.8 intervals, by date)
 *
 * The flat `shift_start` / `shift_end` columns are a weekly summary; they are
 * read only when the technician has no per-day map at all (FLOYD-7's one safe
 * case), never over a real map.
 */

import { resolveDatedShiftEnvelope } from './datedScheduleEnvelope.ts';
import { parseTimeToMinutes } from './canonicalTimeMath.ts';
import { makeShopWindowInstants } from './timezoneMath.ts';
import {
  calendarDaysBetween,
  DEFAULT_ROLLOVER_SEARCH_DAYS,
  nextCalendarDate,
  type PlannerDay,
  type PlannerInterval,
  type ResolveWorkDay,
  type RolloverPlan,
} from './rolloverSegmentPlanner.ts';

export interface RolloverTechnicianInput {
  hours?: unknown;
  time_off?: string[] | null;
  /** Weekly summary — read ONLY for a technician with no per-day map at all. */
  shift_start?: string | null;
  shift_end?: string | null;
  recurring_breaks?: unknown;
  non_recurring_breaks?: unknown;
}

export interface RolloverShopInput {
  holidays?: Array<{ date?: string | null }> | null;
  /** Partial-day absences for THIS technician, keyed by shop-local date. */
  absencesByDate?: Map<string, PlannerInterval[]> | Record<string, PlannerInterval[]>;
}

function breakIntervals(raw: unknown, dateStr: string | null): PlannerInterval[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((b: any) => b && (dateStr === null || b.date === dateStr))
    .map((b: any) => ({ start: parseTimeToMinutes(b.start), end: parseTimeToMinutes(b.end) }))
    .filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end) && b.end > b.start);
}

export function buildTechnicianWorkDayResolver(
  tech: RolloverTechnicianInput,
  shop: RolloverShopInput = {},
): ResolveWorkDay {
  const holidays = new Set((shop.holidays ?? []).map((h) => h?.date).filter(Boolean) as string[]);
  const absences = shop.absencesByDate;
  const absencesFor = (d: string): PlannerInterval[] =>
    (absences instanceof Map ? absences.get(d) : absences?.[d]) ?? [];

  // FLOYD-7's one safe fallback: the flat columns speak ONLY for a technician
  // with no per-day map at all. A real map that omits a day means "not
  // working", and is never overridden by the weekly summary.
  const hasPerDayMap = !!tech.hours && typeof tech.hours === 'object'
    && Object.keys(tech.hours as Record<string, unknown>).length > 0;
  const flatOpen = parseTimeToMinutes(String(tech.shift_start ?? ''));
  const flatClose = parseTimeToMinutes(String(tech.shift_end ?? ''));
  const flatWindow = !hasPerDayMap && Number.isFinite(flatOpen) && Number.isFinite(flatClose) && flatClose > flatOpen
    ? { openMin: flatOpen, closeMin: flatClose }
    : null;
  const timeOff = new Set(tech.time_off ?? []);

  return (dateStr: string): PlannerDay | null => {
    if (holidays.has(dateStr)) return null;
    let window: { openMin: number; closeMin: number } | null = null;
    if (flatWindow) {
      if (!timeOff.has(dateStr)) window = flatWindow;
    } else {
      const env = resolveDatedShiftEnvelope(
        { hours: tech.hours, time_off: tech.time_off ?? [] },
        dateStr,
      );
      if (env.state === 'scheduled' && env.openMinute !== null && env.closeMinute !== null
        && env.closeMinute > env.openMinute) {
        window = { openMin: env.openMinute, closeMin: env.closeMinute };
      }
    }
    if (!window) return null;
    return {
      dateStr,
      openMin: window.openMin,
      closeMin: window.closeMin,
      unavailable: [
        ...breakIntervals(tech.recurring_breaks, null),
        ...breakIntervals(tech.non_recurring_breaks, dateStr),
        ...absencesFor(dateStr),
      ],
    };
  };
}

/**
 * The same resolver, loaded from the database for one technician — for a
 * server path that holds only a ticket and a technician id (the live EOD
 * carry). Reads the technician's dated hours, time off and breaks, the shop's
 * holidays, and the technician's partial-day absences from `fromDateStr` over
 * the planner's whole search horizon.
 */
export async function loadTechnicianWorkDayResolver(
  // deno-lint-ignore no-explicit-any
  supabase: any,
  input: { shopId: string; techId: string; fromDateStr: string; days?: number },
  // oct20d (PR #13 item C): `strict` — an unreadable holiday list or absence list THROWS instead of resolving days
  // with none (a continuation planned onto a shop holiday / over a partial absence). Opt-in so the other callers keep
  // their behaviour until their owners adopt it (listed in docs/field-test-2026-10-20/oct20d-roll.md).
  opts: { strict?: boolean } = {},
): Promise<ResolveWorkDay> {
  const horizon = input.days ?? DEFAULT_ROLLOVER_SEARCH_DAYS;
  let until = input.fromDateStr;
  for (let i = 0; i < horizon; i++) until = nextCalendarDate(until);

  const [{ data: tech, error: techErr }, { data: profile, error: profileErr }, { data: absenceRows, error: absenceErr }] = await Promise.all([
    supabase.from('technicians')
      .select('hours, time_off, recurring_breaks, non_recurring_breaks, shift_start, shift_end')
      .eq('id', input.techId).maybeSingle(),
    supabase.from('shop_profile').select('holidays').eq('shop_id', input.shopId).maybeSingle(),
    supabase.from('technician_absence_intervals')
      .select('absence_date, start_time, end_time')
      .eq('shop_id', input.shopId)
      .eq('technician_id', input.techId)
      .gte('absence_date', input.fromDateStr)
      .lte('absence_date', until),
  ]);
  if (techErr || !tech) throw new Error(`rollover planner: technician ${input.techId} not readable`);
  if (opts.strict && profileErr) throw new Error(`rollover planner: shop holidays not readable: ${profileErr.message}`);
  if (opts.strict && absenceErr) throw new Error(`rollover planner: technician absences not readable: ${absenceErr.message}`);

  const absencesByDate = new Map<string, PlannerInterval[]>();
  for (const row of (absenceRows ?? []) as Array<{ absence_date?: string; start_time?: string; end_time?: string }>) {
    const start = parseTimeToMinutes(String(row.start_time ?? ''));
    const end = parseTimeToMinutes(String(row.end_time ?? ''));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !row.absence_date) continue;
    const d = String(row.absence_date).slice(0, 10);
    absencesByDate.set(d, [...(absencesByDate.get(d) ?? []), { start, end }]);
  }
  return buildTechnicianWorkDayResolver(tech as RolloverTechnicianInput, {
    holidays: (profile?.holidays as Array<{ date?: string | null }> | null) ?? [],
    absencesByDate,
  });
}

/** One part of a rollover plan, as smart-assign returns it. */
export interface RolloverPartResponse {
  partLabel: string;
  /** 1-based chain position; 1 = Part A. */
  sequence: number;
  /** Calendar days after Part A's date — a skipped day off shows as a gap. */
  dayOffset: number;
  scheduledDate: string;
  startTime: string;
  finishTime: string;
  /** Technician OptiTime placed in this part. */
  minutes: number;
  remainingAfterMinutes: number;
}

export function partLabelFor(sequence: number): string {
  // A..Z, then AA, AB, … — a chain never runs out of names.
  let n = sequence;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return `Part ${s}`;
}

export function planToRolloverParts(plan: RolloverPlan, shopTimezone: string): RolloverPartResponse[] {
  const first = plan.segments[0]?.dateStr;
  return plan.segments.map((s) => {
    // Core-100 R4 wave 2 (CORE-AC Rel, DST): start instant + elapsed window minutes.
    const w = makeShopWindowInstants(s.dateStr, s.startMin, s.finishMin, shopTimezone);
    return {
      partLabel: partLabelFor(s.sequence),
      sequence: s.sequence,
      dayOffset: first ? calendarDaysBetween(first, s.dateStr) : 0,
      scheduledDate: s.dateStr,
      startTime: w.start.toISOString(),
      finishTime: w.finish.toISOString(),
      minutes: s.workMinutes,
      remainingAfterMinutes: s.remainingAfterMinutes,
    };
  });
}

/**
 * The workdays a client may need to re-plan the same work from a LATER start
 * (a past-time bump between ranking and creation): every day the plan used,
 * plus the next `extraWorkdays` workdays after it.
 */
export function replanWorkdays(
  plan: RolloverPlan,
  resolveDay: ResolveWorkDay,
  extraWorkdays = 3,
  maxCalendarDays = 30,
): PlannerDay[] {
  const days = [...plan.days];
  let d = days.length > 0 ? nextCalendarDate(days[days.length - 1].dateStr) : null;
  let added = 0;
  for (let i = 0; d && i < maxCalendarDays && added < extraWorkdays; i++) {
    const day = resolveDay(d);
    if (day && day.closeMin > day.openMin) { days.push(day); added++; }
    d = nextCalendarDate(d);
  }
  return days;
}
